import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ChevronRight, Play } from "lucide-react";
import { api } from "../lib/api";
import logger from "../lib/logger";
import { getDateParts } from "../lib/dateUtils";
import { trackEvent } from "../lib/analytics";
import HomeLightboxes from "../components/HomeLightboxes";
import InlineEmailSignup from "../components/InlineEmailSignup";
import FanQuestionForm from "../components/FanQuestionForm";

export default function Home() {
  const [content, setContent] = useState({});
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Seed data on first load
        await api.seedData().catch(() => {});
        
        // Fetch data with individual error handling for mobile compatibility
        let contentData = {};
        let eventsData = [];

        try {
          contentData = await api.getAllContent();
        } catch (err) {
          logger.warn("Content fetch failed, using defaults:", err.message);
        }

        try {
          eventsData = await api.getEvents();
        } catch (err) {
          logger.warn("Events fetch failed:", err.message);
        }

        setContent(contentData || {});
        
        // Get upcoming events (future dates first)
        const today = new Date().toISOString().split('T')[0];
        const upcoming = (eventsData || [])
          .filter(e => e.date >= today)
          .sort((a, b) => a.date.localeCompare(b.date))
          .slice(0, 3);
        setEvents(upcoming);
      } catch (err) {
        logger.warn("Home data fetch error:", err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-demolition-black flex items-center justify-center">
        <div className="animate-pulse text-demolition-red font-russo text-2xl">LOADING...</div>
      </div>
    );
  }

  return (
    <div className="bg-demolition-black" data-testid="home-page">
      <HomeLightboxes />
      {/* Hero Section */}
      <section 
        className="relative min-h-[80vh] flex items-center justify-center overflow-hidden"
        data-testid="hero-section"
      >
        {/* Background */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_classic-demolition/artifacts/cd3h9vht_Gemini_Generated_Image_yun58cyun58cyun5.png')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-demolition-black/80 via-demolition-black/60 to-demolition-black" />
        <div className="absolute inset-0 steel-mesh opacity-30" />
        
        {/* Content */}
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <h1 
            className="font-metal text-5xl sm:text-7xl lg:text-9xl text-white uppercase tracking-tight mb-6 text-shadow-glow"
            data-testid="hero-title"
          >
            AX AND SMASH
          </h1>
          <p className="font-teko text-2xl sm:text-3xl lg:text-4xl text-demolition-chrome uppercase tracking-widest mb-8">
            Here Comes The Ax, Here Comes The Smasher
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/about"
              data-testid="hero-cta-about"
              className="inline-flex items-center gap-2 px-8 py-3 bg-demolition-red text-white font-teko text-xl uppercase tracking-widest hover:bg-demolition-red-hover transition-colors btn-skew"
            >
              <span>Learn More</span>
              <ChevronRight className="w-5 h-5" />
            </Link>
            <Link
              to="/youtube"
              data-testid="hero-cta-youtube"
              onClick={() => trackEvent("youtube_cta", "/youtube", { source: "home_hero" })}
              className="inline-flex items-center gap-2 px-8 py-3 border-2 border-demolition-chrome text-demolition-chrome font-teko text-xl uppercase tracking-widest hover:bg-demolition-chrome hover:text-demolition-black transition-colors btn-skew"
            >
              <Play className="w-5 h-5" />
              <span>Watch DemoPod</span>
            </Link>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-demolition-chrome rounded-none flex justify-center pt-2">
            <div className="w-1 h-3 bg-demolition-chrome" />
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-24 px-6 md:px-12 max-w-7xl mx-auto" data-testid="intro-section">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <div className="border-l-4 border-demolition-red pl-6">
              <h2 className="font-russo text-3xl sm:text-4xl text-white uppercase tracking-tighter mb-4">
                WELCOME TO THE OFFICIAL FAN PAGE
              </h2>
              <p className="font-mono text-white/70 leading-relaxed">
                {content.homepage_intro || "Welcome to the Official Demolition Fan Page - your ultimate destination for everything Ax & Smash!"}
              </p>
            </div>
          </div>
          <div className="relative">
            <div className="aspect-video bg-demolition-paper border border-white/10 overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_classic-demolition/artifacts/6ydkdknv_Golden%20Demolition.png"
                alt="Demolition"
                className="w-full h-full object-cover img-grayscale"
              />
            </div>
            <div className="absolute -bottom-4 -right-4 w-24 h-24 border-4 border-demolition-red bg-demolition-black flex items-center justify-center">
              <span className="font-russo text-3xl text-demolition-red">D</span>
            </div>
          </div>
        </div>
      </section>

      {/* Inline Email Opt-in */}
      <InlineEmailSignup />

      {/* Upcoming Events */}
      <section className="py-24 bg-demolition-paper" data-testid="events-section">
        <div className="px-6 md:px-12 max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-12">
            <h2 className="font-russo text-3xl sm:text-4xl text-white uppercase tracking-tighter">
              UPCOMING EVENTS
            </h2>
            <Link 
              to="/events" 
              className="font-teko text-lg text-demolition-red hover:text-demolition-chrome transition-colors flex items-center gap-2"
              data-testid="view-all-events-link"
            >
              VIEW ALL <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
          
          {events.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-6">
              {events.map((event) => (
                <Link
                  key={event.id}
                  to={`/events/${event.slug}`}
                  className="group bg-demolition-subtle border border-white/10 p-6 hover:border-demolition-red/50 transition-colors"
                  data-testid={`event-card-${event.slug}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="bg-demolition-red p-3 text-center min-w-[60px]">
                      <span className="font-russo text-2xl text-white block">
                        {getDateParts(event.date).day}
                      </span>
                      <span className="font-teko text-sm text-white/80 uppercase">
                        {getDateParts(event.date).month}
                      </span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-teko text-xl text-white uppercase tracking-wide group-hover:text-demolition-red transition-colors">
                        {event.title}
                      </h3>
                      <div className="flex items-center gap-2 mt-2 text-white/50">
                        <MapPin className="w-4 h-4" />
                        <span className="font-mono text-sm">{event.location}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-white/50 font-mono">
              No upcoming events at this time. Check back soon!
            </div>
          )}
        </div>
      </section>

      {/* Fan Questions for DemoPod */}
      <FanQuestionForm />
    </div>
  );
}
